import {rootState} from '@mintdata-wsp/common/state/RootState';
import {
    FlashBlockRequest,
    FlashBlockType,
    FlowBlock,
    FlowBlockId,
    FlowCanvasState,
    FlowWire
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {FlowEvent} from '@mintdata-wsp/wsp/controllers/visualDesigner/workbench/flow/FlowEventsController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {action} from 'mobx';

export class FlowSelectionController
{
    @action.bound
    public clearSelectedWire():void
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        canvas.selectedWire = null;
    }

    public isSelectedWire(wire:FlowWire):boolean
    {
        const selectedWire = this.getSelectedWire();
        return selectedWire && wire && selectedWire.id === wire.id;
    }

    public getSelectedWire():FlowWire | null
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        const {selectedWire} = canvas;

        return selectedWire;
    }

    @action.bound
    public setSelectedWire(flowWire:FlowWire):void
    {
        const {canvas} = rootState.ui.visualDesigner.flow;
        canvas.selectedWire = flowWire;

        this.clearSelectedBlocks();
    }

    public isBlockToFlash(blockId:FlowBlockId):boolean
    {
        const request = this._getState().flashBlock.getRequest();
        return request && request.flowBlockId === blockId;
    }

    public getBlockToFlash():FlashBlockRequest | null
    {
        return this._getState().flashBlock.getRequest();
    }

    public setDoneFlashingBlock(animationDelayInMs:number):void
    {
        this._getState().flashBlock.markRequestDone(animationDelayInMs);
    }

    @action.bound
    public requestFlashBlock(blockId:FlowBlockId, type:FlashBlockType):void
    {
        this._getState().flashBlock.request(new FlashBlockRequest(blockId, type));
    }

    @action.bound
    public clearSelectedBlocks(manually:boolean = true)
    {
        const state = this._getState();
        state.selectedBlocks = [];

        const event = FlowEvent.selectedBlockChanged(this._getSelectedBlockIds(), manually);
        controllers.ui.flowEvents.fireEvent(event);
    }

    public getAnySingleSelectedBlock():FlowBlock | null
    {
        const selectedBlocks = this.getSelectedBlocks();
        if( !selectedBlocks || selectedBlocks.length !== 1 )
            return null;

        return selectedBlocks[0];
    }

    public getSingleSelectedBlock(isShouldBeVisualLinkBlock:boolean):FlowBlock | null
    {
        const selectedBlock = this.getAnySingleSelectedBlock();
        if( !selectedBlock )
            return null;

        if( isShouldBeVisualLinkBlock )
            return selectedBlock.isVisualLink ? selectedBlock : null;
        else
            return !selectedBlock.isVisualLink ? selectedBlock : null;
    }

    public isSingleVisualLinkBlockSelected():boolean
    {
        return this.getSingleSelectedBlock(true) !== null;
    }

    public getSelectedBlocks():FlowBlock[]
    {
        const state = this._getState();
        return state.selectedBlocks;
    }

    @action.bound
    public setSingleBlockSelection(block:FlowBlock, manually:boolean = true):void
    {
        const state = this._getState();
        state.selectedBlocks = [block];

        const event = FlowEvent.selectedBlockChanged(this._getSelectedBlockIds(), manually);
        controllers.ui.flowEvents.fireEvent(event);

        this.clearSelectedWire();
    }

    @action.bound
    public setMultipleBlockSelection(blocks:FlowBlock[], manually:boolean = true):void
    {
        const state = this._getState();
        state.selectedBlocks = blocks;

        const event = FlowEvent.selectedBlockChanged(this._getSelectedBlockIds(), manually);
        controllers.ui.flowEvents.fireEvent(event);

        this.clearSelectedWire();
    }

    @action.bound
    public addBlockToSelection(block:FlowBlock, manually:boolean = true):void
    {
        const state = this._getState();
        state.selectedBlocks.push(block);

        const event = FlowEvent.selectedBlockChanged(this._getSelectedBlockIds(), manually);
        controllers.ui.flowEvents.fireEvent(event);

        this.clearSelectedWire();
    }

    @action.bound
    public removeBlockFromSelection(block:FlowBlock, manually:boolean = true):void
    {
        const state = this._getState();
        const idx = this._findSelectedBoxIndex(state, block);

        if( idx !== -1 )
        {
            state.selectedBlocks.splice(idx, 1);

            const event = FlowEvent.selectedBlockChanged(this._getSelectedBlockIds(), manually);
            controllers.ui.flowEvents.fireEvent(event);

            this.clearSelectedWire();
        }
    }

    public isBlockSelected(block:FlowBlock):boolean
    {
        const state = this._getState();
        const idx = this._findSelectedBoxIndex(state, block);

        return idx !== -1;
    }

    private _getSelectedBlockIds():FlowBlockId[]
    {
        return this._getState().selectedBlocks.map(({id}) => id);
    }

    private _findSelectedBoxIndex(state:FlowCanvasState, block:FlowBlock):number | -1
    {
        const idx = state.selectedBlocks.findIndex((b:FlowBlock) => (b.id === block.id));
        return idx;
    }

    private _getState():FlowCanvasState
    {
        return rootState.ui.visualDesigner.flow.canvas;
    }
}
